import React, { useState, useEffect } from 'react';
import axios from 'axios';
import PanelBreadcrumbs from './PanelBreadcrumbs';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const Categories = ({ setActivePage }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isActive, setIsActive] = useState('');
  const [parentId, setParentId] = useState('');
  const [search, setSearch] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCategories();
    }, search ? 500 : 0); // Debounce search

    return () => clearTimeout(timer);
  }, [page, limit, isActive, parentId, search]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get auth token from localStorage
      const authData = localStorage.getItem('superadmin_auth');
      const token = authData ? JSON.parse(authData).token : null;
      
      // Build query parameters
      const params = {
        page,
        limit,
      };
      
      if (isActive !== '') {
        params.is_active = isActive === 'true';
      }
      if (parentId) {
        params.parent_id = parentId === 'null' ? null : parseInt(parentId);
      }
      if (search) params.search = search;
      
      const response = await axios.get(`${API_BASE_URL}/api/superadmin/categories`, {
        params,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Handle response - adjust based on actual API response structure
      const data = response.data;
      
      if (data.categories && Array.isArray(data.categories)) {
        setCategories(data.categories);
      } else if (Array.isArray(data)) {
        setCategories(data);
      } else if (data.data && Array.isArray(data.data)) {
        setCategories(data.data);
      }
      
      // Handle pagination metadata - check multiple possible formats
      if (data.pagination) {
        setTotalPages(data.pagination.total_pages || data.pagination.totalPages || 1);
        setTotalItems(data.pagination.total || data.pagination.total_items || data.pagination.totalItems || 0);
      } else {
        if (data.total_pages !== undefined) {
          setTotalPages(data.total_pages);
        } else if (data.totalPages !== undefined) {
          setTotalPages(data.totalPages);
        } else if (data.total !== undefined && limit) {
          setTotalPages(Math.ceil(data.total / limit));
        } else {
          setTotalPages(1);
        }
        
        if (data.total !== undefined) {
          setTotalItems(data.total);
        } else if (data.total_items !== undefined) {
          setTotalItems(data.total_items);
        } else if (data.totalItems !== undefined) {
          setTotalItems(data.totalItems);
        } else {
          const currentItems = data.categories?.length || data.data?.length || (Array.isArray(data) ? data.length : 0);
          setTotalItems(currentItems > 0 ? currentItems : 0);
        }
      }
    } catch (err) {
      // Don't show error if session expired (401) - user will be redirected to login
      if (err.isSessionExpired || (err.response && err.response.status === 401)) {
        return;
      }
      console.error('Error fetching categories:', err);
      setError(err.response?.data?.error || 'Failed to load categories');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterType, value) => {
    // Reset to page 1 when filters change
    setPage(1);
    if (filterType === 'is_active') setIsActive(value);
    if (filterType === 'parent_id') setParentId(value);
    if (filterType === 'search') setSearch(value);
  };

  const clearFilters = () => {
    setPage(1);
    setIsActive('');
    setParentId('');
    setSearch('');
  };

  if (loading && categories.length === 0) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
            <p className="text-gray-600">Loading categories...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <PanelBreadcrumbs current="Categories" setActivePage={setActivePage} />
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Categories</h1>
          <p className="text-gray-600 mt-1">Manage product categories</p>
        </div>
        <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium">
          + Add Category
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              value={search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="Search in category name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Active Status</label>
            <select
              value={isActive}
              onChange={(e) => handleFilterChange('is_active', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Parent Category ID</label>
            <input
              type="text"
              value={parentId}
              onChange={(e) => handleFilterChange('parent_id', e.target.value)}
              placeholder="null for root categories"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-red-700">{error}</span>
            <button
              onClick={fetchCategories}
              className="ml-auto px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Categories Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Parent Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Products</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {categories.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                    No categories found
                  </td>
                </tr>
              ) : (
                categories.map((category, index) => (
                  <tr key={category.id || category.category_id || index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {category.name || category.category_name || '-'}
                      </div>
                      {category.description && (
                        <div className="text-xs text-gray-500 mt-1">{category.description}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {category.parent_name || category.parent?.name || (category.parent_id === null || category.parent_id === 0 ? 'Root' : `ID: ${category.parent_id}`)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        (category.is_active === true || category.is_active === 'true' || category.status === 'active')
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {(category.is_active === true || category.is_active === 'true' || category.status === 'active') ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {category.product_count || category.products_count || category.products || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button className="text-indigo-600 hover:text-indigo-900 mr-3">View</button>
                      <button className="text-gray-600 hover:text-gray-900">Edit</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing page {page} of {totalPages || 1} {totalItems > 0 && `(${totalItems} total categories)`}
            {categories.length > 0 && ` - Showing ${categories.length} items`}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-4 py-2 text-sm text-gray-700">
              Page {page} / {totalPages || 1}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages || 1, p + 1))}
              disabled={page >= (totalPages || 1) || loading}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>

        {/* Items per page selector */}
        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Items per page:
          </div>
          <select
            value={limit}
            onChange={(e) => {
              setLimit(parseInt(e.target.value));
              setPage(1);
            }}
            className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      </div>
    </div>
  );
};

export default Categories;
